import { Box, Button, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { CalendarToday, MyLocation, PlaceOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { StatusBadge } from '@/shared/components';
import { AlertListItem } from '../types';

interface AlertCardProps {
  alert: AlertListItem;
  onEdit: (id: string) => void;
  onDisable: (id: string) => void;
}

function formatDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

export function AlertCard({ alert, onEdit, onDisable }: AlertCardProps) {
  const { t } = useTranslation();

  const datesDisplay =
    alert.availabilityEnabled && alert.startDate && alert.endDate
      ? t('alerts.card.dates', {
          from: formatDate(alert.startDate),
          to: formatDate(alert.endDate),
        })
      : null;

  const radiusDisplay =
    alert.maxDistance !== null ? t('alerts.card.radius', { distance: alert.maxDistance }) : null;

  return (
    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }} data-testid={`alert-card-${alert.id}`}>
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Title row: material name + condition badge */}
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
        >
          <Tooltip title={alert.materialName.length > 50 ? alert.materialName : ''} placement="top">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                mr: 1,
                minWidth: 0,
              }}
              data-testid={`alert-card-title-${alert.id}`}
            >
              {alert.materialName}
            </Typography>
          </Tooltip>
          {alert.condition && (
            <Box data-testid={`alert-card-condition-badge-${alert.id}`}>
              <StatusBadge status={alert.condition} size="small" />
            </Box>
          )}
        </Box>

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <PlaceOutlined sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            data-testid={`alert-card-address-${alert.id}`}
          >
            {alert.address}
          </Typography>
        </Box>

        {/* Radius */}
        {radiusDisplay && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <MyLocation sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid={`alert-card-radius-${alert.id}`}
            >
              {radiusDisplay}
            </Typography>
          </Box>
        )}

        {/* Dates */}
        {datesDisplay && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <CalendarToday sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid={`alert-card-dates-${alert.id}`}
            >
              {datesDisplay}
            </Typography>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            fullWidth
            onClick={() => onEdit(alert.id)}
            data-testid={`alert-card-edit-${alert.id}`}
            sx={{ borderRadius: 1 }}
          >
            {t('alerts.card.edit')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            fullWidth
            onClick={() => onDisable(alert.id)}
            data-testid={`alert-card-disable-${alert.id}`}
            sx={{ borderRadius: 1 }}
          >
            {t('alerts.card.disable')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
